import React, { useState } from 'react';
import { Trash2, Archive, AlertTriangle, X } from 'lucide-react';

/**
 * Delete Invoice Modal
 * Shows hard/soft delete options based on invoice status.
 * - Draft/New/Pending Approval → can hard or soft delete
 * - Approved/Posted/other → can only soft delete
 */
export default function DeleteInvoiceModal({ invoice, onConfirm, onClose }) {
  const [deleteType, setDeleteType] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const invoiceNumber = invoice?.InvoiceNumber || 'Unknown';
  const status = invoice?.Status || '';
  
  // Hard delete only allowed for Draft, New, Pending Approval
  const canHardDelete = ['Draft', 'New', 'Pending Approval'].includes(status);

  const handleConfirm = async () => {
    if (!deleteType) return;
    setConfirming(true);
    try {
      await onConfirm(deleteType);
    } catch {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Invoice</h3>
              <p className="text-sm text-gray-500">{invoiceNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-red-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Choose how you want to delete invoice <strong>{invoiceNumber}</strong> (Status: <span className="font-medium">{status}</span>):
          </p>

          {/* Option: Hard Delete */}
          {canHardDelete && (
            <button
              onClick={() => setDeleteType('hard')}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                deleteType === 'hard'
                  ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                  : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <Trash2 className={`w-5 h-5 mt-0.5 ${deleteType === 'hard' ? 'text-red-600' : 'text-gray-400'}`} />
                <div>
                  <p className="font-semibold text-gray-900">Permanent Delete</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Permanently removes the invoice, all line items, and the uploaded document. 
                    <span className="text-red-600 font-medium"> This cannot be undone.</span>
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Option: Soft Delete */}
          <button
            onClick={() => setDeleteType('soft')}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              deleteType === 'soft'
                ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
            }`}
          >
            <div className="flex items-start gap-3">
              <Archive className={`w-5 h-5 mt-0.5 ${deleteType === 'soft' ? 'text-amber-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-semibold text-gray-900">Archive (Soft Delete)</p>
                <p className="text-sm text-gray-500 mt-1">
                  Moves the invoice to the deleted items list. It can be restored later by a Finance Admin.
                </p>
              </div>
            </div>
          </button>

          {/* Info for approved invoices */}
          {!canHardDelete && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                This invoice has been <strong>{status.toLowerCase()}</strong> and can only be archived (soft deleted) for audit trail purposes.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={confirming}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!deleteType || confirming}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
              !deleteType || confirming
                ? 'bg-gray-300 cursor-not-allowed'
                : deleteType === 'hard'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {confirming ? (
              <>
                <span className="animate-spin">⏳</span>
                Deleting...
              </>
            ) : deleteType === 'hard' ? (
              <>
                <Trash2 className="w-4 h-4" />
                Delete Permanently
              </>
            ) : deleteType === 'soft' ? (
              <>
                <Archive className="w-4 h-4" />
                Archive Invoice
              </>
            ) : (
              'Select an option'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
